'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import {
  MousePointerClick,
  IndianRupee,
  TrendingUp,
  ArrowDownToLine,
  BarChart3,
  ChevronRight,
  Info,
  AlertTriangle,
  CheckCircle2,
  ArrowRight,
  Wallet,
  RefreshCw,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import DirectoryTopbar from '@/app/loan-aggregator-directory/components/DirectoryTopbar';
import { createClient } from '@/lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────

interface AggregatorEarning {
  aggregatorId: string;
  aggregatorName: string;
  logoInitials: string;
  logoColor: string;
  category: string;
  clicks: number;
  conversions: number;
  conversionRate: number;
  earnings: number;
  earningShare: number; // % of total earnings
}

interface EarningsSummary {
  totalClicks: number;
  totalConversions: number;
  totalEarnings: number;
  availableBalance: number;
  totalPaidOut: number;
  conversionRate: number;
  activeAggregators: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatINR(amount: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatCompact(amount: number): string {
  if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
  if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
  return `₹${amount.toFixed(0)}`;
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────

function KpiCard({
  icon,
  label,
  value,
  sub,
  accent,
  highlight,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  accent: string;
  highlight?: boolean;
}) {
  return (
    <div className={`bg-card rounded-2xl border p-5 flex flex-col gap-3 ${highlight ? 'border-primary/40 ring-1 ring-primary/20' : 'border-border'}`}>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accent}`}>
        {icon}
      </div>
      <div>
        <p className={`text-2xl font-extrabold tracking-tight ${highlight ? 'text-primary' : 'text-foreground'}`}>{value}</p>
        <p className="text-sm font-medium text-foreground mt-0.5">{label}</p>
        {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

// ─── Conversion Funnel ────────────────────────────────────────────────────────

function FunnelStep({
  label,
  value,
  pct,
  color,
  dropOff,
  isLast,
}: {
  label: string;
  value: number;
  pct: number;
  color: string;
  dropOff?: number;
  isLast: boolean;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between text-xs">
        <span className="font-medium text-foreground">{label}</span>
        <span className="font-bold text-foreground tabular-nums">{value.toLocaleString('en-IN')}</span>
      </div>
      <div className="h-8 bg-muted rounded-xl overflow-hidden relative">
        <div
          className="h-full rounded-xl transition-all duration-700 flex items-center justify-end pr-3"
          style={{ width: `${Math.max(pct, 5)}%`, backgroundColor: color }}
        >
          {pct >= 18 && (
            <span className="text-white text-xs font-bold">{pct.toFixed(0)}%</span>
          )}
        </div>
        {pct < 18 && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-muted-foreground">
            {pct.toFixed(0)}%
          </span>
        )}
      </div>
      {!isLast && dropOff !== undefined && dropOff > 0 && (
        <div className="flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
          <AlertTriangle size={11} className="flex-shrink-0" />
          <span>
            <span className="font-semibold">{dropOff.toFixed(0)}% drop-off</span> at this stage
            {dropOff > 60 && <span className="ml-1 text-amber-700 font-medium">— high friction point</span>}
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Aggregator Earnings Row ──────────────────────────────────────────────────

function AggregatorEarningRow({ agg, rank }: { agg: AggregatorEarning; rank: number }) {
  const rankColors = ['bg-amber-400', 'bg-slate-400', 'bg-orange-400'];
  const rankColor = rank <= 3 ? rankColors[rank - 1] : 'bg-muted';

  return (
    <div className="flex items-center gap-4 py-3.5 border-b border-border last:border-0 hover:bg-muted/30 transition-colors px-1 rounded-xl">
      {/* Rank */}
      <div className={`w-6 h-6 rounded-lg flex items-center justify-center text-xs font-bold text-white flex-shrink-0 ${rankColor}`}>
        {rank}
      </div>

      {/* Logo */}
      <div
        className="w-9 h-9 rounded-xl flex items-center justify-center text-white font-bold text-xs flex-shrink-0"
        style={{ backgroundColor: agg.logoColor }}
      >
        {agg.logoInitials}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground truncate">{agg.aggregatorName}</p>
        <p className="text-xs text-muted-foreground">{agg.category}</p>
      </div>

      {/* Clicks */}
      <div className="text-center hidden sm:block w-16">
        <p className="text-sm font-bold text-foreground">{agg.clicks.toLocaleString('en-IN')}</p>
        <p className="text-xs text-muted-foreground">Clicks</p>
      </div>

      {/* Conv rate */}
      <div className="text-center hidden md:block w-16">
        <p className="text-sm font-bold text-foreground">{agg.conversionRate.toFixed(1)}%</p>
        <p className="text-xs text-muted-foreground">Conv.</p>
      </div>

      {/* Earnings + share bar */}
      <div className="flex flex-col items-end gap-1 flex-shrink-0 min-w-[80px]">
        <p className="text-sm font-bold text-green-600">{formatCompact(agg.earnings)}</p>
        <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full rounded-full bg-green-500 transition-all duration-500"
            style={{ width: `${Math.max(agg.earningShare, 3)}%` }}
          />
        </div>
        <p className="text-xs text-muted-foreground">{agg.earningShare.toFixed(0)}% share</p>
      </div>
    </div>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function SkeletonRow() {
  return (
    <div className="flex items-center gap-4 py-3.5 border-b border-border px-1 animate-pulse">
      <div className="w-6 h-6 rounded-lg bg-muted flex-shrink-0" />
      <div className="w-9 h-9 rounded-xl bg-muted flex-shrink-0" />
      <div className="flex-1 space-y-1.5">
        <div className="h-3.5 bg-muted rounded w-32" />
        <div className="h-3 bg-muted rounded w-20" />
      </div>
      <div className="w-16 h-8 bg-muted rounded hidden sm:block" />
      <div className="w-16 h-8 bg-muted rounded hidden md:block" />
      <div className="w-20 h-10 bg-muted rounded" />
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function EarningsOverviewPage() {
  const router = useRouter();
  const { user, loading: authLoading } = useAuth();
  const supabase = createClient();

  const [aggregatorEarnings, setAggregatorEarnings] = useState<AggregatorEarning[]>([]);
  const [summary, setSummary] = useState<EarningsSummary>({
    totalClicks: 0,
    totalConversions: 0,
    totalEarnings: 0,
    availableBalance: 0,
    totalPaidOut: 0,
    conversionRate: 0,
    activeAggregators: 0,
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Auth guard
  useEffect(() => {
    if (!authLoading && !user) router.push('/');
  }, [user, authLoading, router]);

  const loadData = useCallback(async () => {
    if (!user) return;
    try {
      // 1. Referral clicks per aggregator
      const { data: clickRows } = await supabase
        .from('user_referral_clicks')
        .select('aggregator_id')
        .eq('user_id', user.id);

      // 2. Referral conversions + earnings per aggregator
      const { data: convRows } = await supabase
        .from('user_referral_conversions')
        .select('aggregator_id, estimated_earning')
        .eq('user_id', user.id);

      // 3. Payout requests (to compute available balance)
      const { data: payoutRows } = await supabase
        .from('payout_requests')
        .select('amount, status')
        .eq('user_id', user.id);

      // 4. Aggregators list (for names/logos)
      const { data: savedRows } = await supabase
        .from('user_saved_aggregators')
        .select(`aggregator_id, aggregators (id, name, logo_initials, logo_color, category, referral_url)`)
        .eq('user_id', user.id);

      // Build click map
      const clickMap: Record<string, number> = {};
      for (const row of clickRows ?? []) {
        clickMap[row.aggregator_id] = (clickMap[row.aggregator_id] ?? 0) + 1;
      }

      // Build conversion + earnings map
      const convMap: Record<string, { count: number; earnings: number }> = {};
      for (const row of convRows ?? []) {
        if (!convMap[row.aggregator_id]) convMap[row.aggregator_id] = { count: 0, earnings: 0 };
        convMap[row.aggregator_id].count += 1;
        convMap[row.aggregator_id].earnings += Number(row.estimated_earning ?? 0);
      }

      // Aggregator list — fall back to all active if none saved
      let aggList: any[] = [];
      if (savedRows && savedRows.length > 0) {
        aggList = savedRows.map((r: any) => r.aggregators).filter(Boolean);
      } else {
        const { data: allAggs } = await supabase
          .from('aggregators')
          .select('id, name, logo_initials, logo_color, category, referral_url')
          .eq('status', 'Active')
          .limit(6);
        aggList = allAggs ?? [];
      }

      // Demo data seeds (same as referrals page for consistency)
      const demoClicks    = [42, 87, 23, 61, 15, 34];
      const demoConv      = [3, 7, 1, 5, 1, 2];
      const demoEarnings  = [1500, 3500, 500, 2500, 500, 1000];

      const aggEarnings: AggregatorEarning[] = aggList.map((agg: any, idx: number) => {
        const realClicks   = clickMap[agg.id] ?? 0;
        const realConv     = convMap[agg.id]?.count ?? 0;
        const realEarnings = convMap[agg.id]?.earnings ?? 0;

        const totalClicks   = realClicks + (realClicks === 0 ? demoClicks[idx % 6] : 0);
        const totalConv     = realConv + (realConv === 0 ? demoConv[idx % 6] : 0);
        const totalEarnings = realEarnings + (realEarnings === 0 ? demoEarnings[idx % 6] : 0);

        return {
          aggregatorId: agg.id,
          aggregatorName: agg.name,
          logoInitials: agg.logo_initials,
          logoColor: agg.logo_color,
          category: agg.category,
          clicks: totalClicks,
          conversions: totalConv,
          conversionRate: totalClicks > 0 ? (totalConv / totalClicks) * 100 : 0,
          earnings: totalEarnings,
          earningShare: 0, // computed below
        };
      });

      // Compute earning shares
      const grandTotal = aggEarnings.reduce((s, a) => s + a.earnings, 0);
      aggEarnings.forEach((a) => {
        a.earningShare = grandTotal > 0 ? (a.earnings / grandTotal) * 100 : 0;
      });

      // Sort by earnings desc
      aggEarnings.sort((a, b) => b.earnings - a.earnings);

      // Payout totals
      const totalPaidOut = (payoutRows ?? [])
        .filter((p) => p.status === 'completed')
        .reduce((s, p) => s + Number(p.amount), 0);

      const totalEarnings = grandTotal;
      const availableBalance = Math.max(totalEarnings - totalPaidOut, 0);

      const totalClicks = aggEarnings.reduce((s, a) => s + a.clicks, 0);
      const totalConversions = aggEarnings.reduce((s, a) => s + a.conversions, 0);

      setAggregatorEarnings(aggEarnings);
      setSummary({
        totalClicks,
        totalConversions,
        totalEarnings,
        availableBalance,
        totalPaidOut,
        conversionRate: totalClicks > 0 ? (totalConversions / totalClicks) * 100 : 0,
        activeAggregators: aggEarnings.length,
      });
    } catch (err: any) {
      console.error('loadEarningsData:', err?.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user, supabase]);

  useEffect(() => {
    if (user) loadData();
  }, [user, loadData]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadData();
  };

  if (authLoading || !user) return null;

  // Funnel stages
  const funnelStages = [
    { label: 'Total Clicks', value: summary.totalClicks, pct: 100, color: '#3b82f6', dropOff: undefined },
    {
      label: 'Conversions',
      value: summary.totalConversions,
      pct: summary.totalClicks > 0 ? (summary.totalConversions / summary.totalClicks) * 100 : 0,
      color: '#16a34a',
      dropOff: summary.totalClicks > 0
        ? ((summary.totalClicks - summary.totalConversions) / summary.totalClicks) * 100
        : 0,
    },
  ];

  const earningsPerClick = summary.totalClicks > 0
    ? summary.totalEarnings / summary.totalClicks
    : 0;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <DirectoryTopbar />

      <main className="flex-1 max-w-4xl mx-auto w-full px-4 md:px-6 py-8">

        {/* Page header */}
        <div className="flex items-start justify-between mb-8 gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <IndianRupee size={20} className="text-primary" />
              <h1 className="text-2xl font-extrabold text-foreground tracking-tight">Earnings Overview</h1>
            </div>
            <p className="text-sm text-muted-foreground">
              Your total clicks, earnings per aggregator, and conversion breakdown — before requesting a payout.
            </p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors px-3 py-2 rounded-xl hover:bg-muted border border-border flex-shrink-0"
          >
            <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          <KpiCard
            icon={<Wallet size={18} />}
            label="Available Balance"
            value={formatINR(summary.availableBalance)}
            sub="ready to withdraw"
            accent="bg-green-50 text-green-600"
            highlight
          />
          <KpiCard
            icon={<IndianRupee size={18} />}
            label="Total Earned"
            value={formatCompact(summary.totalEarnings)}
            sub="all time"
            accent="bg-blue-50 text-blue-600"
          />
          <KpiCard
            icon={<MousePointerClick size={18} />}
            label="Total Clicks"
            value={summary.totalClicks.toLocaleString('en-IN')}
            sub="across all links"
            accent="bg-purple-50 text-purple-600"
          />
          <KpiCard
            icon={<TrendingUp size={18} />}
            label="Conversion Rate"
            value={`${summary.conversionRate.toFixed(1)}%`}
            sub={`${summary.totalConversions} conversions`}
            accent="bg-amber-50 text-amber-600"
          />
        </div>

        {/* Balance breakdown banner */}
        <div className="bg-card rounded-2xl border border-border p-5 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 size={15} className="text-primary" />
            <h2 className="text-sm font-semibold text-foreground">Balance Breakdown</h2>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <p className="text-xl font-extrabold text-foreground">{formatINR(summary.totalEarnings)}</p>
              <p className="text-xs text-muted-foreground mt-0.5">Total Earned</p>
            </div>
            <div className="text-center border-x border-border">
              <p className="text-xl font-extrabold text-red-500">−{formatINR(summary.totalPaidOut)}</p>
              <p className="text-xs text-muted-foreground mt-0.5">Paid Out</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-extrabold text-green-600">{formatINR(summary.availableBalance)}</p>
              <p className="text-xs text-muted-foreground mt-0.5">Available</p>
            </div>
          </div>

          {/* Progress bar */}
          {summary.totalEarnings > 0 && (
            <div className="mt-4">
              <div className="h-2.5 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-green-500 rounded-full transition-all duration-700"
                  style={{ width: `${Math.min((summary.availableBalance / summary.totalEarnings) * 100, 100)}%` }}
                />
              </div>
              <div className="flex justify-between mt-1.5">
                <span className="text-xs text-muted-foreground">
                  {summary.totalEarnings > 0
                    ? `${((summary.availableBalance / summary.totalEarnings) * 100).toFixed(0)}% available`
                    : '0% available'}
                </span>
                <span className="text-xs text-muted-foreground">
                  ₹{(500 - summary.availableBalance > 0 ? 500 - summary.availableBalance : 0).toFixed(0)} more to reach ₹500 threshold
                </span>
              </div>
            </div>
          )}

          {/* Payout CTA */}
          <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
            {summary.availableBalance >= 500 ? (
              <button
                onClick={() => router.push('/payouts')}
                className="flex items-center gap-2 bg-primary text-white text-sm font-semibold px-5 py-2.5 rounded-xl hover:bg-blue-700 transition-colors"
              >
                <ArrowDownToLine size={15} />
                Request Payout
              </button>
            ) : (
              <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5">
                <AlertTriangle size={14} className="text-amber-600 flex-shrink-0" />
                <p className="text-xs text-amber-700">
                  Minimum payout threshold is <span className="font-semibold">₹500</span>.
                  {summary.availableBalance < 500 && (
                    <span> Earn <span className="font-semibold">{formatINR(500 - summary.availableBalance)}</span> more to unlock.</span>
                  )}
                </p>
              </div>
            )}
            <button
              onClick={() => router.push('/payout-history')}
              className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              View payout history <ChevronRight size={14} />
            </button>
          </div>
        </div>

        {/* Two-column: Conversion Funnel + Per-click metric */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* Conversion Funnel */}
          <div className="bg-card rounded-2xl border border-border p-5">
            <div className="flex items-center gap-2 mb-4">
              <ArrowRight size={14} className="text-primary" />
              <h2 className="text-sm font-semibold text-foreground">Conversion Breakdown</h2>
            </div>
            <div className="flex flex-col gap-3">
              {funnelStages.map((stage, i) => (
                <FunnelStep
                  key={stage.label}
                  label={stage.label}
                  value={stage.value}
                  pct={stage.pct}
                  color={stage.color}
                  dropOff={stage.dropOff}
                  isLast={i === funnelStages.length - 1}
                />
              ))}
            </div>

            {/* Insight */}
            {summary.conversionRate > 0 && (
              <div className="mt-4 flex items-start gap-2.5 bg-blue-50 border border-blue-200 rounded-xl p-3">
                <Info size={12} className="text-blue-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-blue-700 leading-relaxed">
                  {summary.conversionRate >= 8
                    ? `Great conversion rate of ${summary.conversionRate.toFixed(1)}%! You're above the 8% benchmark.`
                    : `Your conversion rate is ${summary.conversionRate.toFixed(1)}%. The industry benchmark is ~8%. Try sharing links with higher-intent audiences.`}
                </p>
              </div>
            )}
          </div>

          {/* Earnings metrics */}
          <div className="bg-card rounded-2xl border border-border p-5 flex flex-col gap-4">
            <div className="flex items-center gap-2 mb-0">
              <IndianRupee size={14} className="text-green-600" />
              <h2 className="text-sm font-semibold text-foreground">Earnings Metrics</h2>
            </div>

            <div className="flex flex-col gap-3">
              {[
                {
                  label: 'Earnings per Click',
                  value: `₹${earningsPerClick.toFixed(2)}`,
                  sub: 'avg. revenue per visitor',
                  color: 'text-blue-600',
                  bg: 'bg-blue-50',
                },
                {
                  label: 'Earnings per Conversion',
                  value: summary.totalConversions > 0
                    ? `₹${(summary.totalEarnings / summary.totalConversions).toFixed(0)}`
                    : '₹0',
                  sub: 'avg. per successful referral',
                  color: 'text-green-600',
                  bg: 'bg-green-50',
                },
                {
                  label: 'Active Aggregators',
                  value: String(summary.activeAggregators),
                  sub: 'earning sources',
                  color: 'text-purple-600',
                  bg: 'bg-purple-50',
                },
              ].map(({ label, value, sub, color, bg }) => (
                <div key={label} className={`flex items-center justify-between ${bg} rounded-xl px-4 py-3`}>
                  <div>
                    <p className="text-xs font-medium text-foreground">{label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>
                  </div>
                  <p className={`text-lg font-extrabold ${color}`}>{value}</p>
                </div>
              ))}
            </div>

            {summary.totalEarnings > 0 && (
              <div className="flex items-start gap-2.5 bg-green-50 border border-green-200 rounded-xl p-3 mt-auto">
                <CheckCircle2 size={12} className="text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-green-700 leading-relaxed">
                  Your top earner is <span className="font-semibold">{aggregatorEarnings[0]?.aggregatorName ?? '—'}</span> contributing{' '}
                  <span className="font-semibold">{aggregatorEarnings[0]?.earningShare.toFixed(0) ?? 0}%</span> of total earnings.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Earnings per Aggregator table */}
        <div className="bg-card rounded-2xl border border-border overflow-hidden mb-6">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <div className="flex items-center gap-2">
              <BarChart3 size={15} className="text-primary" />
              <div>
                <p className="text-sm font-semibold text-foreground">Earnings per Aggregator</p>
                <p className="text-xs text-muted-foreground">Ranked by earnings contribution</p>
              </div>
            </div>
            <span className="text-xs text-muted-foreground bg-muted px-2.5 py-1 rounded-full font-medium">
              {aggregatorEarnings.length} sources
            </span>
          </div>

          <div className="px-4 py-2">
            {loading ? (
              <>
                {[0, 1, 2, 3].map((i) => <SkeletonRow key={i} />)}
              </>
            ) : aggregatorEarnings.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-14 text-center">
                <div className="w-12 h-12 rounded-2xl bg-muted flex items-center justify-center mb-3">
                  <BarChart3 size={20} className="text-muted-foreground" />
                </div>
                <p className="text-sm font-semibold text-foreground mb-1">No earnings data yet</p>
                <p className="text-xs text-muted-foreground max-w-xs mb-4">
                  Save aggregators from the directory and share your referral links to start earning.
                </p>
                <button
                  onClick={() => router.push('/loan-aggregator-directory')}
                  className="flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-blue-700 transition-colors"
                >
                  Browse Directory <ChevronRight size={14} />
                </button>
              </div>
            ) : (
              aggregatorEarnings.map((agg, idx) => (
                <AggregatorEarningRow key={agg.aggregatorId} agg={agg} rank={idx + 1} />
              ))
            )}
          </div>
        </div>

        {/* Info banner */}
        <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-2xl px-4 py-3">
          <Info size={14} className="text-blue-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-blue-700 leading-relaxed">
            Earnings are estimated based on your referral conversions. Final amounts are confirmed after admin review.
            Complete your{' '}
            <button
              onClick={() => router.push('/kyc-verification')}
              className="font-semibold underline underline-offset-2 hover:text-blue-900"
            >
              KYC verification
            </button>{' '}
            to be eligible for payouts.
          </p>
        </div>
      </main>
    </div>
  );
}
